/**
 * Approval Page
 *
 * Opened from the signed link emailed when a workflow run pauses at a
 * human-input node. Deliberately unauthenticated: the approver may be an
 * external CFO with no app account. Authorization is the link token itself —
 * an expiring HMAC scoped to exactly one node of one run — which we hand
 * straight back to the public approvals endpoint.
 */

import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CheckCircle, XCircle, AlertCircle, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'

type Decision = 'approve' | 'reject'

type PendingApproval = {
  workflowName?: string | null
  prompt?: string | null
  pending: boolean
}

/** What the approver is currently looking at. */
type View =
  | { kind: 'loading' }
  | { kind: 'pending'; approval: PendingApproval }
  | { kind: 'resolved'; decision: Decision }
  | { kind: 'handled' }
  | { kind: 'error'; message: string }

export function ApprovePage() {
  const [params] = useSearchParams()
  const executionId = params.get('executionId') ?? ''
  const nodeId = params.get('nodeId') ?? ''
  const token = params.get('token') ?? ''
  // `intent` records which button they clicked in the email. It only preselects
  // the highlighted action — the decision is never applied without an explicit
  // click here, so an email scanner prefetching the link can't approve anything.
  const intent: Decision = params.get('intent') === 'reject' ? 'reject' : 'approve'

  const [view, setView] = useState<View>({ kind: 'loading' })
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const endpoint = `/api/webhook/approvals/${encodeURIComponent(executionId)}`

  useEffect(() => {
    if (!executionId || !nodeId || !token) {
      setView({ kind: 'error', message: 'This approval link is incomplete.' })
      return
    }

    let cancelled = false
    const load = async () => {
      try {
        const res = await fetch(
          `${endpoint}?nodeId=${encodeURIComponent(nodeId)}&token=${encodeURIComponent(token)}`,
        )
        const body = await res.json().catch(() => null)
        if (cancelled) return

        if (!res.ok || !body?.success) {
          setView({
            kind: 'error',
            message: body?.message ?? 'This approval link is invalid or has expired.',
          })
          return
        }
        setView(
          body.data.pending
            ? { kind: 'pending', approval: body.data }
            : { kind: 'handled' },
        )
      } catch {
        if (!cancelled) {
          setView({ kind: 'error', message: 'Could not reach the server. Please try again.' })
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [endpoint, executionId, nodeId, token])

  const submit = useCallback(
    async (decision: Decision) => {
      setSubmitting(true)
      try {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ nodeId, token, decision, note: note.trim() }),
        })
        const body = await res.json().catch(() => null)

        if (res.status === 409) {
          setView({ kind: 'handled' })
          return
        }
        if (!res.ok || !body?.success) {
          setView({
            kind: 'error',
            message: body?.message ?? 'Could not submit your decision.',
          })
          return
        }
        setView({ kind: 'resolved', decision })
      } catch {
        setView({ kind: 'error', message: 'Could not reach the server. Please try again.' })
      } finally {
        setSubmitting(false)
      }
    },
    [endpoint, nodeId, token, note],
  )

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-lg" data-testid="approve-card">
        {view.kind === 'loading' && (
          <>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-32" />
            </CardHeader>
            <CardContent className="space-y-3">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </>
        )}

        {view.kind === 'pending' && (
          <>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-amber-500" />
                Approval requested
              </CardTitle>
              <CardDescription>
                {view.approval.workflowName ?? 'A workflow run'} is waiting for your decision.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p
                className="rounded-md border bg-muted/50 p-3 text-sm"
                data-testid="approve-prompt"
              >
                {view.approval.prompt ?? 'A run is waiting for your input.'}
              </p>

              <div className="space-y-2">
                <Label htmlFor="approval-note">Note (optional)</Label>
                <Textarea
                  id="approval-note"
                  data-testid="approve-note"
                  rows={2}
                  placeholder="e.g. verified against the signed contract"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  data-testid="approve-submit"
                  disabled={submitting}
                  variant={intent === 'approve' ? 'default' : 'outline'}
                  onClick={() => submit('approve')}
                >
                  Approve
                </Button>
                <Button
                  data-testid="approve-reject"
                  disabled={submitting}
                  variant={intent === 'reject' ? 'destructive' : 'outline'}
                  onClick={() => submit('reject')}
                >
                  Reject
                </Button>
              </div>
            </CardContent>
          </>
        )}

        {view.kind === 'resolved' && (
          <CardHeader>
            <CardTitle
              className="flex items-center gap-2"
              data-testid="approve-resolved"
            >
              {view.decision === 'approve' ? (
                <>
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                  Approved
                </>
              ) : (
                <>
                  <XCircle className="h-5 w-5 text-destructive" />
                  Rejected
                </>
              )}
            </CardTitle>
            <CardDescription>
              The workflow has resumed and will act on your decision. You can close this page.
            </CardDescription>
          </CardHeader>
        )}

        {view.kind === 'handled' && (
          <CardHeader>
            <CardTitle className="flex items-center gap-2" data-testid="approve-handled">
              <AlertCircle className="h-5 w-5 text-muted-foreground" />
              Already handled
            </CardTitle>
            <CardDescription>
              This request was already answered, or the run has moved on.
            </CardDescription>
          </CardHeader>
        )}

        {view.kind === 'error' && (
          <CardHeader>
            <CardTitle className="flex items-center gap-2" data-testid="approve-error">
              <XCircle className="h-5 w-5 text-destructive" />
              Link invalid or expired
            </CardTitle>
            <CardDescription>{view.message}</CardDescription>
          </CardHeader>
        )}
      </Card>
    </div>
  )
}
